import type { ReactNode } from 'react';
import { PickerGlyphSvg } from './pickerIcons';

/** A clickable starter prompt shown under the composer on a fresh chat. */
export interface StarterSuggestion {
  /** A lucide glyph name (see pickerIcons GLYPHS), e.g. 'mail' — rendered as a
   *  native line-icon that tints with the theme via currentColor. */
  icon: string;
  label: string;
  /** Text dropped into the composer when clicked (usually a lead-in to finish). */
  prompt: string;
}

interface Props {
  greeting?: string;
  /** One-line tagline under the greeting (brand mock). */
  subtitle?: string;
  /** The composer, rendered centered inside the hero on a fresh chat. */
  composer?: ReactNode;
  /** Starter prompts shown under the composer; clicking one fires onPickSuggestion. */
  suggestions?: StarterSuggestion[];
  onPickSuggestion?: (prompt: string) => void;
}

export function EmptyState({ greeting, subtitle, composer, suggestions, onPickSuggestion }: Props) {
  return (
    <div className="empty-state" id="emptyState">
      <div className="empty-state__center">
        <h2 className="empty-state__greeting">{greeting ?? 'Where should we begin?'}</h2>
        {subtitle && <p className="empty-state__sub">{subtitle}</p>}
        {composer}
        {suggestions && suggestions.length > 0 && (
          <div className="empty-state__suggestions" role="list" aria-label="Starter prompts">
            {suggestions.map((s) => (
              <button
                key={s.label}
                type="button"
                role="listitem"
                className="empty-state__chip"
                onClick={() => onPickSuggestion?.(s.prompt)}
              >
                <span className="empty-state__chip-icon" aria-hidden="true">
                  <PickerGlyphSvg icon={s.icon} label={s.label} size={17} strokeWidth={1.7} />
                </span>
                <span className="empty-state__chip-label">{s.label}</span>
              </button>
            ))}
          </div>
        )}
        <p className="empty-state__hint">
          <span className="empty-state__hint-item"><kbd>/</kbd> wrapper</span>
          <span className="empty-state__hint-item"><kbd>Enter</kbd> send</span>
          <span className="empty-state__hint-item"><kbd>⇧</kbd>+<kbd>Enter</kbd> new line</span>
        </p>
      </div>
    </div>
  );
}
